export type AutofixCandidate = {
  _id?: unknown;
  status?: unknown;
  filePath?: unknown;
  rawValue?: unknown;
  suggestedToken?: unknown;
  suggestion?: unknown;
  prop?: unknown;
  line?: unknown;
  ruleId?: unknown;
  code?: unknown;
  message?: unknown;
  fixJson?: unknown;
};

export type SafeAutofixFinding = {
  _id: string;
  filePath: string;
  rawValue: string;
  suggestedToken?: string;
  replacementValue: string;
  prop?: string;
  line?: number;
  ruleId: string;
  code?: string;
  message: string;
};

function parseJsonRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== 'string' || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function hasDeterministicAutofix(fixJson: unknown): boolean {
  const fix = parseJsonRecord(fixJson);
  if (fix.deterministic !== true) return false;
  if (fix.patch && typeof fix.patch === 'object') return true;
  if (Array.isArray(fix.edits) && fix.edits.length > 0) return true;
  if (Array.isArray(fix.replacements) && fix.replacements.length > 0) {
    return true;
  }
  return (
    nonEmptyString(fix.value) ||
    nonEmptyString(fix.to) ||
    nonEmptyString(fix.replaceWith)
  );
}

function deterministicReplacementValue(fixJson: unknown): string | null {
  const fix = parseJsonRecord(fixJson);
  if (fix.deterministic !== true) return null;
  if (nonEmptyString(fix.value)) return fix.value;
  if (nonEmptyString(fix.to)) return fix.to;
  if (nonEmptyString(fix.replaceWith)) return fix.replaceWith;
  const patch = isPlainObject(fix.patch) ? fix.patch : null;
  if (patch && nonEmptyString(patch.to)) return patch.to;
  if (patch && nonEmptyString(patch.replacement)) return patch.replacement;
  return null;
}

export function isSafeAutofixCandidate(candidate: AutofixCandidate): boolean {
  return (
    candidate.status === 'open' &&
    nonEmptyString(candidate._id) &&
    nonEmptyString(candidate.filePath) &&
    nonEmptyString(candidate.rawValue) &&
    hasDeterministicAutofix(candidate.fixJson) &&
    (nonEmptyString(candidate.suggestedToken) ||
      deterministicReplacementValue(candidate.fixJson) !== null)
  );
}

export function toAutofixFinding(
  candidate: AutofixCandidate,
): SafeAutofixFinding | null {
  if (!isSafeAutofixCandidate(candidate)) return null;
  const replacementValue =
    (nonEmptyString(candidate.suggestedToken)
      ? candidate.suggestedToken
      : deterministicReplacementValue(candidate.fixJson)) ?? '';
  return {
    _id: candidate._id as string,
    filePath: candidate.filePath as string,
    rawValue: candidate.rawValue as string,
    ...(nonEmptyString(candidate.suggestedToken)
      ? { suggestedToken: candidate.suggestedToken }
      : {}),
    replacementValue,
    prop: nonEmptyString(candidate.prop) ? candidate.prop : undefined,
    line: typeof candidate.line === 'number' ? candidate.line : undefined,
    ruleId: nonEmptyString(candidate.ruleId) ? candidate.ruleId : 'unknown',
    code: nonEmptyString(candidate.code) ? candidate.code : undefined,
    message: nonEmptyString(candidate.message) ? candidate.message : '',
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}
